import { Link } from 'gatsby'
import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useEventListener } from 'react-recipes'
import scrollManager from '~/utils/scrollManager'
import trads from '~/intl/trads.json'
import PanelNav from '~/components/features/header/panelNav'
import PanelProfile from '~/components/features/header/panelProfile'
import Labo from '~/components/features/labo/labo'
import PhoneNumber from '~/components/features/header/phoneNumber'
import ButtonClose from '~/components/core/buttonClose'
import { User } from '~/icons/User'
import { ChevronDown } from '~/icons/ChevronDown'
import { Burger } from '~/icons/Burger'
import {
  changeLang,
  setHasFixedProfileNav,
  setHasLaboOpen,
  setHasMainNavOpen,
  setHasProfileNavOpen,
} from '~/store/actions'
import { useEffect } from 'react'
import { MagnifyingGlass } from '~/icons/MagnifyingGlass'
import {QuestionMark} from '~/icons/QuestionMark'
import { combineReducers } from 'redux'
import {Bulb} from "~/icons/Bulb";
import {Book} from "~/icons/Book";
const rootReducer = combineReducers({})
export type IRootState = ReturnType<typeof rootReducer>

interface HeaderProps {
  frUrl: string
  enUrl: string
  noBg: boolean
  needReloading: boolean
  headerWhite: boolean
  learningSpace:  { fr: Queries.ContentfulLinkPage; en: Queries.ContentfulLinkPage }
}

const Header = ({
  frUrl,
  enUrl,
  noBg,
  needReloading,
  headerWhite,
  learningSpace
}: HeaderProps) => {
  // local state
  const [hasLangOpen, setHasLangOpen] = useState(false)
  const [isFixed, setIsFixed] = useState(false)

  // redux
  const lang = useSelector((state: IRootState) => state.lang)
  const hasLaboOpen = useSelector((state: IRootState) => state.hasLaboOpen)
  const hasMainNavOpen = useSelector(
    (state: IRootState) => state.hasMainNavOpen,
  )
  const hasProfileNavOpen = useSelector(
    (state: IRootState) => state.hasProfileNavOpen,
  )
  const dispatch = useDispatch()

  // ref
  const headerContent = React.createRef<HTMLDivElement>()

  // managed fixed header
  const handleScroll = () => {
    if (headerContent.current !== null) {
      let checkIfFixed =
        window.pageYOffset > (headerContent.current.offsetTop as number)
          ? true
          : false
      setIsFixed(checkIfFixed)
    }
  }

  const calcHeightMobile = () => {
    const vh = window.innerHeight * 0.01
    document.body.style.setProperty('--vh', `${vh}px`)
  }

  useEffect(() => {
    dispatch(setHasLaboOpen(false))
    calcHeightMobile()
  }, [dispatch])

  // close all panels on logo click
  const closePanels = () => {
    dispatch(setHasLaboOpen(false))
    setHasLangOpen(false)
    dispatch(setHasMainNavOpen(false))
    dispatch(setHasProfileNavOpen(false))
    setHasFixedProfileNav(false)
  }

  // toggle main nav panel or profile
  const togglePanel = (type: string) => {
    if (type === 'nav') {
      dispatch(setHasMainNavOpen(!hasMainNavOpen))
    } else if (type === 'profile') {
      dispatch(setHasProfileNavOpen(!hasProfileNavOpen))
    } else {
      dispatch(setHasLaboOpen(!hasLaboOpen))

      if (!hasLaboOpen) {
        // TODO: refactor if possible
        setTimeout(() => {
          document.querySelector('#laboInput').focus()
        }, 50)
      }
    }

    if (
      (!hasMainNavOpen && type === 'nav') ||
      (!hasProfileNavOpen && type === 'profile') ||
      (!hasLaboOpen && type === 'labo')
    ) {
      setTimeout(() => {
        scrollManager.stop()
      }, 400)
    } else {
      scrollManager.restart()
    }
  }

  // scroll event listener
  useEventListener('scroll', handleScroll)
  useEventListener('resize', calcHeightMobile)
  useEventListener('orientationchange', calcHeightMobile)

  return (
    <>
      <header className={`header ${hasMainNavOpen && 'is-open'}`}>
        <div className="bg-dark-purple text-13 font-semi-bold">
          <div className="fluid-grid">
            <div className="flex justify-between items-center">
              <PhoneNumber />

              <div className="text-white flex items-center">
                <Link
                  to={lang === 'fr' ? '/contact' : '/en/contact'}
                  className="mr-10 hide-mobile"
                >
                  {trads[lang]?.header?.contact}
                </Link>

                <div
                  className={`relative hide-mobile self-stretch mr-9 ${hasLangOpen ? 'z-12' : 'z-2'}`}
                >
                  <button
                    className={`header__lang flex items-center h-full ${hasLangOpen && 'is-active'}`}
                    onClick={() => setHasLangOpen(!hasLangOpen)}
                  >
                    <span className="pointer-events-none">
                      {lang === 'fr' ? 'Fr' : 'En'}
                    </span>
                    <ChevronDown className="icon-white flex-shrink-0 pointer-events-none" />
                  </button>
                  <div className="header__lang-dropdown">
                    {needReloading ? (
                      <>
                        <a
                          className={`${lang === 'fr' ? 'is-active mb-4' : 'mb-4'} ${frUrl === null && 'is-disabled'}`}
                          onClick={() => dispatch(changeLang('fr'))}
                          href={frUrl === null ? '/' : frUrl}
                        >
                          Fr
                        </a>
                        <a
                          className={`${lang === 'en' ? 'is-active' : ''} ${enUrl === null && 'is-disabled'}`}
                          onClick={() => dispatch(changeLang('en'))}
                          href={enUrl === null ? '/en' : enUrl}
                        >
                          En
                        </a>
                      </>
                    ) : (
                      <>
                        <Link
                          className={`${lang === 'fr' ? 'is-active mb-4' : 'mb-4'} ${frUrl === null && 'is-disabled'}`}
                          onClick={() => dispatch(changeLang('fr'))}
                          to={frUrl === null ? '/' : frUrl}
                        >
                          Fr
                        </Link>
                        <Link
                          className={`${lang === 'en' ? 'is-active' : ''} ${enUrl === null && 'is-disabled'}`}
                          onClick={() => dispatch(changeLang('en'))}
                          to={enUrl === null ? '/en' : enUrl}
                        >
                          En
                        </Link>
                      </>
                    )}
                  </div>
                </div>
                <a
                  href="https://portail.terega.fr/star-app/accueil"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="header__portal-btn"
                >
                  <User className="icon-white flex-shrink-0" />
                  {trads[lang]?.header?.customerPortal}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`header__content ${isFixed && 'is-fixed'}`}
          ref={headerContent}
        >
          <div className="flex items-center md:items-start">
            {!hasMainNavOpen ? (
              <BurgerOpen
                text={trads[lang]?.header?.burgerClosed}
                color={noBg && !headerWhite ? 'purple' : 'white'}
                action={() => togglePanel('nav')}
              />
            ) : (
              <ButtonClose color="white" action={() => togglePanel('nav')} />
            )}
            <Link
              className="block ml-2 md:ml-12 flex-shrink-0"
              to={lang === 'fr' ? '/' : '/en'}
              onClick={() => closePanels()}
            >
              <img
                className="header__logo"
                src={
                  noBg && !hasMainNavOpen && !hasProfileNavOpen && !headerWhite
                    ? '/img/logo.svg'
                    : '/img/logo-white.svg'
                }
                alt="Terega"
              />
              <img
                src="/img/logo.svg"
                className="header__logo-fixed"
                alt="Teréga"
              />
            </Link>
          </div>

          <div className="flex gap-2 xl:gap-6">
            <a
                className="header__pill bg-medium-purple p-0 aspect-square lg:aspect-auto lg:px-4 grid place-items-center lg:flex"
                href={`${lang === 'en' ? '/en' : ''}/${learningSpace[lang]?.pageLink?.slug}/`}
            >
              <span className="hide-mobile">
                {learningSpace[lang]?.label}
              </span>
              <Book className="stroke-white fill-transparent size-4 flex-shrink-0 lg:ml-2"/>
            </a>
            <button
                className="header__pill bg-purple p-0 aspect-square lg:aspect-auto lg:px-4 grid place-items-center lg:flex"
                onClick={() => togglePanel('labo')}
            >
              <span className="hide-mobile">
                {trads[lang]?.header?.search}
              </span>
              <MagnifyingGlass className="icon-white flex-shrink-0 lg:ml-2 w-4"/>
            </button>
            <button
                className="header__pill header__pill--blue"
                onClick={() => togglePanel('profile')}
            >
              <span className="hidden xl:block">
                {trads[lang]?.header?.profileDesktop}
              </span>
              <span className="xl:hidden" aria-hidden="true">
                {trads[lang]?.header?.profileMobile}
              </span>

              <ChevronDown
                  className="icon-white flex-shrink-0 ml-1 hide-mobile"
                  width="18"
                  height="18"
              />
            </button>
          </div>
        </div>

        <PanelNav frUrl={frUrl} enUrl={enUrl} isOpen={hasMainNavOpen}/>
        <PanelProfile isOpen={hasProfileNavOpen} togglePanel={togglePanel}/>
      </header>
      <Labo isOpen={hasLaboOpen}/>
    </>
  )
}

const BurgerOpen = ({text, color, action}) => {
  return (
      <button className="header__burger" onClick={action}>
        <Burger className={`icon-${color}`}/>
        <span className={`text-${color} hide-mobile`}>{text}</span>
    </button>
  )
}

export default Header
